import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { apiFetch } from '../lib/api'
import {
  PRODUCTS,
  type ContractResponse,
  type Product,
  type ProductQuota,
} from '../lib/types'
import { STATUS_LABEL_KEY, type ProductView } from '../lib/productView'
import { useProductTab } from '../hooks/useProductTab'
import { useProductStatus } from '../hooks/useProductStatus'
import { Tabs } from '../components/ui/Tabs'
import { PageScrollArea, PageView } from '../components/ui/PageScrollArea'
import { PageHeader } from '../components/ui/PageHeader'
import { Card, CardHeader, CardBody } from '../components/ui/Card'
import { Kpi, KpiRow } from '../components/ui/Kpi'
import { ProductViewGate } from '../components/ProductViewGate'
import { TrialBanner } from '../components/TrialBanner'
import { useMoney } from '../hooks/useMoney'
import { BalanceForecastChart } from '../components/BalanceForecastChart'
import { AgentsCreditsBalanceCard } from '../components/AgentsCreditsBalanceCard'
import { InfoTip } from '../components/ui/InfoTip'
import { formatLongDate } from '../lib/months'
import { PRODUCT_INFO, fmtRate } from './productInfo'

/** The Expired Subscription view (MS-819; spec §4.1b): the prototype's
 * `renderExpiredSubscription(prefix)`, reached from `renderStatus` when the
 * status is Expired. The same "Subscription details" card holding only a KPI
 * row — status, then em dashes in the prototype's order, every value at
 * 17px with no units, no sub line and no danger tone — and no package box
 * and no balance card (the prototype hides `#<prefix>-balance-card`). */
function ExpiredSubscriptionDetails({ product }: Readonly<{ product: Product }>) {
  const { t } = useTranslation()
  const status = <Kpi size="compact" label={t('subscription.kpi.subscriptionStatus')} value={t(STATUS_LABEL_KEY.Expired)} />
  const dash = (label: string) => <Kpi size="compact" label={label} value={null} />
  return (
    <Card collapsible>
      <CardHeader
        title={t('subscription.details.title')}
        subtitle={t(product === 'Mambu MCP' ? 'subscription.details.subtitleMcp' : 'subscription.details.subtitleAgt')}
      />
      <CardBody>
        <KpiRow>
          {product === 'Mambu MCP' ? (
            <>
              {status}
              {dash(t('subscription.kpi.contractStartDate'))}
              {dash(t('subscription.kpi.invocationsContracted'))}
              {dash(t('subscription.kpi.invocationsOnDemand'))}
              {dash(t('subscription.kpi.balanceRemaining'))}
              {dash(t('subscription.kpi.excessVolumeRate'))}
            </>
          ) : (
            <>
              {dash(t('subscription.kpi.currentProductTier'))}
              {status}
              {dash(t('subscription.kpi.contractStartDate'))}
              {dash(t('subscription.kpi.invocationsContracted'))}
              {dash(t('subscription.kpi.balanceRemaining'))}
              {dash(t('subscription.kpi.excessVolumeRate'))}
            </>
          )}
        </KpiRow>
      </CardBody>
    </Card>
  )
}

export function Subscription() {
  const { t } = useTranslation()
  const { product, setProduct } = useProductTab('subscription')
  const status = useProductStatus(product)

  return (
    <PageView>
      <PageHeader title={t('subscription.title')} subtitle={t('subscription.subtitle')} />

      <Tabs value={product} onChange={setProduct} options={PRODUCTS} />

      <PageScrollArea>
        <ProductViewGate status={status} page="subscription" product={product}>
          {(view, current) =>
            view === 'expired' ? (
              <ExpiredSubscriptionDetails product={product} />
            ) : (
              <SubscriptionDetails product={product} view={view} current={current} />
            )
          }
        </ProductViewGate>
      </PageScrollArea>
    </PageView>
  )
}

/** The Contracted (`full`) and Trial (`trial`) Subscription views (MS-820,
 * AC1): the prototype's `renderMcpSubscription` / Agents `renderStatus` KPI
 * rows and their trial counterparts (`renderMcpTrialSubscription`,
 * `renderAgtTrialSubscription`, spec §18-trial), the package box (kept, Q12),
 * then the balance card — Invocations Balance for Mambu MCP, Credits Balance
 * (with its simulation) for Mambu Agents; on trial both draw the trial
 * allowance, which does not renew.
 *
 * Every balance figure is /quota's purchases-derived balance (Q11), not a
 * client-side formula, and Contracted Volume is included + committed volume
 * (Q18). A figure that does not exist for the status renders as an em dash,
 * never as 0. */
function SubscriptionDetails({
  product,
  view,
  current,
}: Readonly<{ product: Product; view: Exclude<ProductView, 'preview' | 'expired'>; current: ProductQuota }>) {
  const { t, i18n } = useTranslation()
  const trial = view === 'trial'

  const { data: contract } = useQuery({
    queryKey: ['contract'],
    queryFn: () => apiFetch<ContractResponse>('/api/v1/contract'),
  })
  // Contract dates are per product (they may differ), matched on `product`.
  const subscription = contract?.subscriptions.find((s) => s.product === product)
  const contractStart = subscription?.contractStartDate ?? null
  const contractEnd = subscription?.contractEndDate ?? null

  return (
    <div className="flex flex-col gap-[18px]">
      {/* Decided by status (MS-819 Q6): the `trial` view only resolves from
          a Trial row, which always carries its end date. If one ever slips
          past the constraint the banner is left out rather than dateless. */}
      {trial && current.trialEndDate && (
        <TrialBanner product={product} trialEndDate={formatLongDate(current.trialEndDate, i18n.language)} />
      )}

      <Card collapsible>
        <CardHeader
          title={t('subscription.details.title')}
          subtitle={t(product === 'Mambu MCP' ? 'subscription.details.subtitleMcp' : 'subscription.details.subtitleAgt')}
        />
        <CardBody>
          <SubscriptionKpis product={product} trial={trial} current={current} contractStart={contractStart} />
        </CardBody>
      </Card>

      <BalanceSection
        product={product}
        trial={trial}
        current={current}
        contractStart={contractStart}
        contractEnd={contractEnd}
      />
    </div>
  )
}

/** The excess rate is stored per unit but quoted per 1,000 units, as the
 * prototype does — the same real rate at the denomination a reader thinks in. */
function useExcessRatePer1000(current: ProductQuota): string {
  const money = useMoney()
  const ratePer1000 = Number(current.priceExcess) * 1000
  return money(ratePer1000, fmtRate(ratePer1000).split('.')[1]?.length ?? 0)
}

/** The Subscription Details KPI row, in the prototype's per-product order. */
function SubscriptionKpis({
  product,
  trial,
  current,
  contractStart,
}: Readonly<{ product: Product; trial: boolean; current: ProductQuota; contractStart: string | null }>) {
  const { t, i18n } = useTranslation()
  const excessRatePer1000 = useExcessRatePer1000(current)
  const isMcp = product === 'Mambu MCP'
  const number = (value: number | null) => (value === null ? null : value.toLocaleString(i18n.language))
  const date = (iso: string | null) => (iso === null ? null : formatLongDate(iso, i18n.language))
  // A trial shows no contracted, on-demand or excess figure.
  const unlessTrial = (value: string | null) => (trial ? null : value)

  const statusKpi = (
    <Kpi
      size="compact"
      label={t('subscription.kpi.subscriptionStatus')}
      value={t(trial ? STATUS_LABEL_KEY.Trial : STATUS_LABEL_KEY.Contracted)}
    />
  )
  // A trial has no contract: its second date is when the trial ends, and there
  // is no contract start to show (the prototype's `contractStartLabel`).
  const dateKpi = trial ? (
    <Kpi size="compact" label={t('subscription.kpi.trialEndDate')} value={date(current.trialEndDate)} />
  ) : (
    <Kpi size="compact" label={t('subscription.kpi.contractStartDate')} value={date(contractStart)} />
  )
  const contractedLabel = t('subscription.kpi.invocationsContracted')
  const contractedKpi = (
    <Kpi
      label={
        isMcp || trial ? (
          contractedLabel
        ) : (
          <span className="inline-flex items-center">
            {contractedLabel}
            <InfoTip text={t('subscription.kpi.contractedVolumeAgtInfo')} placement="below" align="left" width={300} />
          </span>
        )
      }
      value={unlessTrial(number(current.contractedVolume))}
      unit={` ${t(isMcp ? 'common.perMonth' : 'common.perYear')}`}
    />
  )
  const balanceKpi = <Kpi label={t('subscription.kpi.balanceRemaining')} value={number(current.balanceRemaining)} />
  // No excess during a trial (§7): the limit is hard, so there is no rate.
  const excessKpi = (
    <Kpi
      label={t('subscription.kpi.excessVolumeRate')}
      value={unlessTrial(excessRatePer1000)}
      unit={` ${t(isMcp ? 'subscription.kpi.perInvocation' : 'subscription.kpi.perCredit')}`}
      tone="danger"
    />
  )

  if (isMcp) {
    return (
      <KpiRow>
        {statusKpi}
        {dateKpi}
        {contractedKpi}
        <Kpi
          label={t('subscription.kpi.invocationsOnDemand')}
          value={unlessTrial(number(current.onDemandMonthly))}
          unit={` ${t('common.perMonth')}`}
        />
        {balanceKpi}
        {excessKpi}
      </KpiRow>
    )
  }
  return (
    <KpiRow>
      <Kpi size="compact" label={t('subscription.kpi.currentProductTier')} value={PRODUCT_INFO[product].tierName} />
      {statusKpi}
      {dateKpi}
      {contractedKpi}
      {balanceKpi}
      {excessKpi}
    </KpiRow>
  )
}

/** The balance card: Credits Balance (with its simulation) for a contracted
 * Mambu Agents subscription, otherwise the balance forecast chart. */
function BalanceSection({
  product,
  trial,
  current,
  contractStart,
  contractEnd,
}: Readonly<{
  product: Product
  trial: boolean
  current: ProductQuota
  contractStart: string | null
  contractEnd: string | null
}>) {
  const { t } = useTranslation()
  const isMcp = product === 'Mambu MCP'
  if (!isMcp && !trial) {
    return <AgentsCreditsBalanceCard quota={current} contractStart={contractStart} contractEnd={contractEnd} />
  }
  return (
    <Card collapsible>
      <CardHeader
        title={t(isMcp ? 'subscription.balance.title' : 'subscription.creditsBalance.title')}
        subtitle={t(isMcp ? 'subscription.balance.subtitle' : 'subscription.creditsBalance.subtitleTrial')}
      />
      <CardBody>
        <BalanceForecastChart product={product} quota={current} trial={trial} />
      </CardBody>
    </Card>
  )
}
